import React, { useState } from "react";
import { adminLogin } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { Loader2, LogOut, ShieldCheck, ShoppingBag, Tag, BarChart3 } from "lucide-react";
import OrdersTab from "@/pages/admin/OrdersTab";
import CouponsTab from "@/pages/admin/CouponsTab";
import AnalyticsTab from "@/pages/admin/AnalyticsTab";

const PASSWORD_KEY = "bv_admin_pw";

function LoginView({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pw) return toast.error("Enter password");
    setBusy(true);
    try {
      await adminLogin(pw);
      sessionStorage.setItem(PASSWORD_KEY, pw);
      onSuccess(pw);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-black px-4">
      <form onSubmit={submit} className="glass w-full max-w-sm rounded-3xl p-8">
        <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-fire text-black">
          <ShieldCheck size={24} />
        </div>
        <h1 className="font-display text-3xl text-white">Admin Access</h1>
        <p className="mt-1 text-sm text-neutral-400">Bajrang Veda command center</p>
        <input
          data-testid={TID.adminPasswordInput}
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Password"
          className="mt-5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none focus:border-[#FF7A00]"
          autoFocus
        />
        <button
          data-testid={TID.adminLoginBtn}
          type="submit"
          disabled={busy}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-fire py-3 text-sm font-bold uppercase tracking-widest text-black disabled:opacity-60"
        >
          {busy ? <Loader2 className="animate-spin" size={16} /> : null} Sign In
        </button>
        <a href="/" className="mt-4 block text-center text-xs text-neutral-500 hover:text-white">← Back to store</a>
      </form>
    </div>
  );
}

const TABS = [
  { id: "orders", label: "Orders", icon: ShoppingBag, testid: TID.adminTabOrders },
  { id: "coupons", label: "Coupons", icon: Tag, testid: TID.adminTabCoupons },
  { id: "analytics", label: "Analytics", icon: BarChart3, testid: TID.adminTabAnalytics },
];

function Shell({ password, onLogout }) {
  const [tab, setTab] = useState("orders");
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-30 border-b border-white/5 bg-black/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-2xl">BAJRANG <span className="text-fire">VEDA</span> · ADMIN</div>
              <div className="text-xs text-neutral-500">Command center</div>
            </div>
            <button
              data-testid={TID.adminLogoutBtn}
              onClick={onLogout}
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs hover:bg-white/10 sm:hidden"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-full border border-white/10 bg-white/5 p-1">
              {TABS.map(({ id, label, icon: Icon, testid }) => (
                <button
                  key={id}
                  data-testid={testid}
                  onClick={() => setTab(id)}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${
                    tab === id ? "bg-fire text-black font-bold" : "text-neutral-400 hover:text-white"
                  }`}
                >
                  <Icon size={13} />
                  {label}
                </button>
              ))}
            </div>
            <button
              data-testid={TID.adminLogoutBtn + "-desktop"}
              onClick={onLogout}
              className="hidden items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs hover:bg-white/10 sm:flex"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        {tab === "orders" && <OrdersTab password={password} onUnauth={onLogout} />}
        {tab === "coupons" && <CouponsTab password={password} onUnauth={onLogout} />}
        {tab === "analytics" && <AnalyticsTab password={password} onUnauth={onLogout} />}
      </main>
    </div>
  );
}

export default function Admin() {
  const [password, setPassword] = useState(() => sessionStorage.getItem(PASSWORD_KEY) || "");
  if (!password) return <LoginView onSuccess={setPassword} />;
  return (
    <Shell
      password={password}
      onLogout={() => {
        sessionStorage.removeItem(PASSWORD_KEY);
        setPassword("");
      }}
    />
  );
}
